import { useEffect, useRef } from 'react';

const LoveAura = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const mouseRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    const targetRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    const timeRef = useRef(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        const handleMouseMove = (e: MouseEvent) => {
            targetRef.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener('mousemove', handleMouseMove);

        // Smooth mouse following with easing
        const updateMousePosition = () => {
            mouseRef.current.x += (targetRef.current.x - mouseRef.current.x) * 0.1;
            mouseRef.current.y += (targetRef.current.y - mouseRef.current.y) * 0.1;
        };

        const animate = () => {
            timeRef.current += 0.01;
            updateMousePosition();

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const { x, y } = mouseRef.current;

            // Draw multiple layered auras around mouse
            for (let i = 5; i > 0; i--) {
                const radius = 50 + i * 30 + Math.sin(timeRef.current + i) * 10;
                const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
                
                // Romantic color palette for auras
                const colors = [
                    { r: 255, g: 182, b: 193, name: 'pink' },
                    { r: 255, g: 218, b: 185, name: 'peach' },
                    { r: 221, g: 160, b: 221, name: 'plum' },
                    { r: 255, g: 228, b: 225, name: 'misty' },
                    { r: 240, g: 230, b: 140, name: 'khaki' }
                ];
                
                const color = colors[i % colors.length];
                const alpha = 0.15 / i;
                
                gradient.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha * 2})`);
                gradient.addColorStop(0.5, `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`);
                gradient.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);
                
                ctx.fillStyle = gradient;
                ctx.beginPath();
                ctx.arc(x, y, radius, 0, Math.PI * 2);
                ctx.fill();
            }

            // Draw floating light particles around mouse
            for (let i = 0; i < 8; i++) {
                const angle = (timeRef.current * 0.5 + i * (Math.PI * 2 / 8));
                const distance = 80 + Math.sin(timeRef.current * 2 + i) * 20;
                const px = x + Math.cos(angle) * distance;
                const py = y + Math.sin(angle) * distance;
                const size = 3 + Math.sin(timeRef.current * 3 + i) * 2;
                
                const particleGradient = ctx.createRadialGradient(px, py, 0, px, py, size * 2);
                particleGradient.addColorStop(0, 'rgba(255, 215, 0, 0.8)');
                particleGradient.addColorStop(0.5, 'rgba(255, 192, 203, 0.5)');
                particleGradient.addColorStop(1, 'rgba(255, 182, 193, 0)');
                
                ctx.fillStyle = particleGradient;
                ctx.beginPath();
                ctx.arc(px, py, size * 2, 0, Math.PI * 2);
                ctx.fill();
            }

            // Draw subtle heart pulse at mouse position
            const heartSize = 15 + Math.sin(timeRef.current * 3) * 3;
            ctx.save();
            ctx.globalAlpha = 0.4 + Math.sin(timeRef.current * 2) * 0.2;
            ctx.fillStyle = '#ff69b4';
            ctx.shadowBlur = 20;
            ctx.shadowColor = 'rgba(255, 105, 180, 0.8)';
            drawHeart(ctx, x, y, heartSize);
            ctx.restore();

            requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none"
            style={{ zIndex: 2 }}
        />
    );
};

const drawHeart = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => {
    ctx.beginPath();
    const topCurveHeight = size * 0.3;
    ctx.moveTo(x, y + topCurveHeight);
    ctx.bezierCurveTo(x, y, x - size / 2, y, x - size / 2, y + topCurveHeight);
    ctx.bezierCurveTo(x - size / 2, y + (size + topCurveHeight) / 2, x, y + (size + topCurveHeight) / 1.2, x, y + size);
    ctx.bezierCurveTo(x, y + (size + topCurveHeight) / 1.2, x + size / 2, y + (size + topCurveHeight) / 2, x + size / 2, y + topCurveHeight);
    ctx.bezierCurveTo(x + size / 2, y, x, y, x, y + topCurveHeight);
    ctx.closePath();
    ctx.fill();
};

export default LoveAura;
